// src/pages/auth/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { FaLock, FaEye, FaEyeSlash, FaKey, FaCheck, FaSpinner, FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { authAPI } from '../../services/api';
import assets from '../../assets';

const ResetPassword = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [step, setStep] = useState('otp'); // 'otp' or 'password'
  const [searchParams] = useSearchParams();
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  useEffect(() => {
    if (!token || !email) {
      toast.error('Invalid reset link. Please request a new password reset.');
      navigate('/forgot-password');
    }
  }, [token, email, navigate]);

  const verifyOTP = async (data) => {
    try {
      setLoading(true);
      const loadingToast = toast.loading('Verifying OTP...');

      const response = await authAPI.verifyResetOTP({
        token,
        email,
        otp: data.otp
      });

      toast.dismiss(loadingToast);

      if (response.success) {
        toast.success('OTP verified successfully!');
        setOtpVerified(true);
        setStep('password');
      } else {
        throw new Error(response.message || 'Invalid OTP code');
      }
    } catch (error) {
      toast.error(error.message || 'Invalid OTP code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (data) => {
    try {
      setLoading(true);
      const loadingToast = toast.loading('Updating your password...');

      const response = await authAPI.resetPassword({
        token,
        email,
        password: data.password
      });

      toast.dismiss(loadingToast);

      if (response.success) {
        toast.success('Password updated successfully!');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to reset password');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data) => {
    if (step === 'otp') {
      verifyOTP(data);
    } else {
      resetPassword(data);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-8 overflow-hidden">
      {/* Background Elements */}
      <div
        className="absolute inset-0 opacity-3"
        style={{
          backgroundImage: `
            radial-gradient(circle at 20% 20%, #22c55e 2px, transparent 2px),
            radial-gradient(circle at 80% 80%, #fbbf24 2px, transparent 2px),
            radial-gradient(circle at 40% 60%, #ef4444 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px, 120px 120px, 60px 60px',
        }}
      />

      {/* Main Container */}
      <div className="relative flex flex-col items-center mx-auto lg:flex-row lg:max-w-5xl xl:max-w-6xl px-4 sm:px-6 lg:px-8 min-h-screen">
        
        {/* Image Column */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="w-full h-64 lg:w-1/2 lg:h-[500px] xl:h-[600px] relative overflow-hidden rounded-2xl mt-8 lg:mt-0"
        >
          <img
            src={assets.gallery?.competitions?.[4] || assets.heroImages?.primary}
            alt="Secure Password Reset"
            className="h-full w-full object-cover"
          />
          
          <div className="absolute top-6 left-6 bg-black/20 backdrop-blur-md rounded-xl px-4 py-2">
            <span className="text-white text-sm font-semibold drop-shadow">
              Secure Password Reset
            </span>
          </div>
        </motion.div>

        {/* Form Column */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="max-w-lg bg-white md:max-w-2xl md:z-10 md:shadow-2xl md:absolute md:top-0 md:mt-32 lg:w-3/5 lg:right-0 lg:mt-20 lg:-ml-20 xl:mt-24 xl:-ml-12 rounded-2xl"
        >
          <div className="flex flex-col p-6 md:p-8">
            
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mb-4"
            >
              <Link
                to="/login"
                className="inline-flex items-center text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
              >
                <FaArrowLeft className="mr-2 w-3 h-3" />
                Back to Login
              </Link>
            </motion.div>

            {step === 'otp' ? (
              <>
                {/* OTP Verification */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="mb-4"
                >
                  <span className="inline-block px-3 py-1.5 bg-purple-100 rounded-full border border-purple-200 text-purple-700 text-xs font-medium mb-4">
                    Step 1: Verify OTP
                  </span>
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.0 }}
                  className="text-xl lg:text-2xl font-bold text-gray-900 leading-tight mb-3"
                >
                  Enter Your{' '}
                  <span className="bg-gradient-to-r from-purple-600 via-blue-600 to-green-600 bg-clip-text text-transparent">
                    OTP Code
                  </span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                  className="text-gray-600 text-sm leading-relaxed mb-6"
                >
                  Please enter the 6-digit OTP code that was sent to <strong>{email}</strong>
                </motion.p>

                <motion.form
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.4 }}
                  onSubmit={handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">OTP Code</label>
                    <div className="relative">
                      <FaKey className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Enter 6-digit OTP"
                        maxLength="6"
                        className="block w-full rounded-2xl border border-gray-300 focus:border-purple-500 focus:ring-purple-500/20 focus:outline-none focus:ring-2 pl-10 pr-4 py-3 text-sm bg-gray-50 transition-colors text-center text-lg font-mono tracking-widest"
                        {...register('otp', {
                          required: 'OTP is required',
                          minLength: { value: 6, message: 'OTP must be 6 digits' },
                          pattern: { value: /^\d{6}$/, message: 'OTP must be 6 digits' }
                        })}
                      />
                    </div>
                    {errors.otp && <p className="text-red-500 text-xs">{errors.otp.message}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-2xl font-bold hover:scale-105 transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="mr-2 w-4 h-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <FaCheck className="mr-2 w-4 h-4" />
                        Verify OTP
                      </>
                    )}
                  </button>
                </motion.form>
              </>
            ) : (
              <>
                {/* Password Reset */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="mb-4"
                >
                  <span className="inline-block px-3 py-1.5 bg-green-100 rounded-full border border-green-200 text-green-700 text-xs font-medium mb-4">
                    Step 2: New Password
                  </span>
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.0 }}
                  className="text-xl lg:text-2xl font-bold text-gray-900 leading-tight mb-3"
                >
                  Create New{' '}
                  <span className="bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Password
                  </span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                  className="text-gray-600 text-sm leading-relaxed mb-6"
                >
                  Choose a strong password that you haven't used before.
                </motion.p>

                <motion.form
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.4 }}
                  onSubmit={handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  {/* New Password */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">New Password</label>
                    <div className="relative">
                      <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter new password"
                        className="block w-full rounded-2xl border border-gray-300 focus:border-green-500 focus:ring-green-500/20 focus:outline-none focus:ring-2 pl-10 pr-10 py-3 text-sm bg-gray-50 transition-colors"
                        {...register('password', {
                          required: 'Password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters' },
                          pattern: {
                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                            message: 'Password must contain uppercase, lowercase, and number'
                          }
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-red-500 text-xs">{errors.password.message}</p>}
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                    <div className="relative">
                      <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm new password"
                        className="block w-full rounded-2xl border border-gray-300 focus:border-green-500 focus:ring-green-500/20 focus:outline-none focus:ring-2 pl-10 pr-10 py-3 text-sm bg-gray-50 transition-colors"
                        {...register('confirmPassword', {
                          required: 'Please confirm your password',
                          validate: (value) => value === watch('password') || 'Passwords do not match'
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="text-red-500 text-xs">{errors.confirmPassword.message}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold hover:scale-105 transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="mr-2 w-4 h-4 animate-spin" />
                        Updating Password...
                      </>
                    ) : (
                      <>
                        <FaLock className="mr-2 w-4 h-4" />
                        Update Password
                      </>
                    )}
                  </button>
                </motion.form>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;